import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MediaPicker from "@/components/admin/MediaPicker";
import ArticlePicker from "@/components/admin/ArticlePicker";
import {
  Plus, Edit3, Trash2, Play, Pause, Eye, Copy, BarChart3, MegaphoneIcon,
  Layers, Target, TrendingUp, Loader2, Image as ImageIcon, Stethoscope,
} from "lucide-react";
import { formatDateShort, formatTime } from "@/lib/date";

const CAMPAIGN_STATUS = [
  { value: "draft", label: "Borrador" },
  { value: "active", label: "Activa" },
  { value: "paused", label: "Pausada" },
  { value: "finished", label: "Finalizada" },
];

const STATUS_CLASS = {
  draft: "bg-lightgray text-textgray",
  active: "bg-watergreen/20 text-nightblue",
  paused: "bg-yellow-100 text-yellow-800",
  finished: "bg-softblue/50 text-nightblue",
};

const CREATIVE_TYPES = [
  { value: "image", label: "Imagen (JPG/PNG/WebP)" },
  { value: "gif", label: "GIF animado" },
  { value: "video", label: "Video (MP4)" },
  { value: "html", label: "Código HTML" },
  { value: "embed", label: "Código embed" },
];

const SLOT_POSITIONS = [
  { value: "header", label: "Encabezado" },
  { value: "below_menu", label: "Debajo del menú" },
  { value: "below_hero", label: "Debajo de la noticia principal" },
  { value: "between_blocks", label: "Entre bloques de portada" },
  { value: "sidebar", label: "Barra lateral" },
  { value: "in_article", label: "Dentro de una noticia" },
  { value: "before_related", label: "Antes de relacionadas" },
  { value: "footer", label: "Pie de página" },
  { value: "floating", label: "Banner flotante" },
  { value: "mobile_exclusive", label: "Exclusivo móvil" },
  { value: "custom", label: "Personalizada" },
];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Argentina/Ushuaia",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
function fromLocalInput(v) { return v ? new Date(`${v}:00-03:00`).toISOString() : null; }

export default function AdminAdvertisingPage() {
  const [tab, setTab] = useState("campaigns");
  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Comercial</div>
        <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Publicidad</h1>
        <p className="mt-1 text-sm text-textgray">Administrá campañas, piezas creativas, ubicaciones y rendimiento.</p>
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-softblue mb-6">
        {[
          { key: "campaigns", label: "Campañas", Icon: MegaphoneIcon },
          { key: "creatives", label: "Piezas", Icon: Layers },
          { key: "slots", label: "Ubicaciones", Icon: Target },
          { key: "stats", label: "Estadísticas", Icon: BarChart3 },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            data-testid={`admin-ads-tab-${key}`}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
              tab === key ? "border-nightblue text-nightblue" : "border-transparent text-textgray hover:text-nightblue"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "campaigns" && <CampaignsTab />}
      {tab === "creatives" && <CreativesTab />}
      {tab === "slots" && <SlotsTab />}
      {tab === "stats" && <StatsTab />}
    </div>
  );
}

// -------------------------- CAMPAIGNS TAB --------------------------
function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    api.adminListCampaigns().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!dialog.name.trim()) { toast.error("El nombre es obligatorio."); return; }
    try {
      const payload = {
        ...dialog,
        start_date: fromLocalInput(dialog.start_date),
        end_date: fromLocalInput(dialog.end_date),
      };
      if (dialog.id) await api.adminUpdateCampaign(dialog.id, payload);
      else await api.adminCreateCampaign(payload);
      toast.success("Campaña guardada.");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    }
  };

  const del = async () => {
    const doDelete = async (force = false) => {
      try {
        await api.adminDeleteCampaign(confirm.id, force);
        toast.success(force ? "Campaña eliminada (piezas incluidas)" : "Campaña eliminada.");
        setConfirm(null); load();
      } catch (e) {
        const status = e?.response?.status;
        const d = e?.response?.data?.detail;
        if (!force && status === 409 && typeof d === "object" && d?.requires_force) {
          const msg = d.message || `Hay ${d.linked_count || "varias"} piezas asociadas.`;
          if (window.confirm(`${msg}\n\n¿Continuar y eliminar todo?`)) return doDelete(true);
          setConfirm(null);
          return;
        }
        toast.error(typeof d === "string" ? d : (d?.message || "No se pudo eliminar."));
      }
    };
    await doDelete(false);
  };

  return (
    <div data-testid="admin-ads-campaigns">
      <div className="flex justify-end mb-4">
        <Button
          data-testid="admin-ads-campaign-new"
          onClick={() => setDialog({ name: "", client: "", description: "", start_date: "", end_date: "", status: "draft", contact_name: "", contact_email: "", contact_phone: "", notes: "" })}
          className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 uppercase text-xs tracking-widest font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva campaña
        </Button>
      </div>

      <div className="bg-white border border-softblue overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lightgray text-textgray text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-3">Nombre</th>
              <th className="text-left px-3 py-3">Cliente</th>
              <th className="text-left px-3 py-3">Estado</th>
              <th className="text-left px-3 py-3">Vigencia</th>
              <th className="text-left px-3 py-3">Piezas</th>
              <th className="text-right px-3 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-textgray">Cargando…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-textgray">Todavía no hay campañas.</td></tr>}
            {!loading && items.map((c) => (
              <tr key={c.id} data-testid={`admin-ads-campaign-row-${c.id}`} className="border-t border-softblue hover:bg-lightgray/40">
                <td className="px-3 py-3">
                  <div className="font-semibold text-blueblack">{c.name}</div>
                  {c.description && <div className="text-xs text-textgray line-clamp-1">{c.description}</div>}
                </td>
                <td className="px-3 py-3 text-textgray">{c.client || "—"}</td>
                <td className="px-3 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${STATUS_CLASS[c.status] || "bg-lightgray"}`}>
                    {CAMPAIGN_STATUS.find((s) => s.value === c.status)?.label || c.status}
                  </span>
                </td>
                <td className="px-3 py-3 text-textgray text-xs">
                  {c.start_date ? formatDateShort(c.start_date) : "—"} → {c.end_date ? formatDateShort(c.end_date) : "sin fin"}
                </td>
                <td className="px-3 py-3 text-textgray">{c.creatives_count || 0}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setDialog({ ...c, start_date: toLocalInput(c.start_date), end_date: toLocalInput(c.end_date) })}
                      data-testid={`admin-ads-campaign-edit-${c.id}`}
                      className="h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray inline-flex items-center justify-center"
                    ><Edit3 className="w-4 h-4" /></button>
                    <button
                      onClick={() => setConfirm(c)}
                      data-testid={`admin-ads-campaign-delete-${c.id}`}
                      className="h-8 w-8 border border-softblue text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">{dialog?.id ? "Editar campaña" : "Nueva campaña"}</DialogTitle>
            <DialogDescription>Datos del cliente y período de la campaña.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Nombre interno *</Label>
                <Input data-testid="admin-ads-campaign-name" value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Cliente</Label>
                <Input value={dialog.client} onChange={(e) => setDialog({ ...dialog, client: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Estado</Label>
                <select data-testid="admin-ads-campaign-status" value={dialog.status} onChange={(e) => setDialog({ ...dialog, status: e.target.value })} className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                  {CAMPAIGN_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción</Label>
                <Textarea value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} rows={2} className="mt-1 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Inicio</Label>
                <Input type="datetime-local" value={dialog.start_date} onChange={(e) => setDialog({ ...dialog, start_date: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Fin</Label>
                <Input type="datetime-local" value={dialog.end_date} onChange={(e) => setDialog({ ...dialog, end_date: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Contacto — nombre</Label>
                <Input value={dialog.contact_name} onChange={(e) => setDialog({ ...dialog, contact_name: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Contacto — email</Label>
                <Input type="email" value={dialog.contact_email} onChange={(e) => setDialog({ ...dialog, contact_email: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Contacto — teléfono</Label>
                <Input value={dialog.contact_phone} onChange={(e) => setDialog({ ...dialog, contact_phone: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Notas</Label>
                <Textarea value={dialog.notes} onChange={(e) => setDialog({ ...dialog, notes: e.target.value })} rows={2} className="mt-1 bg-lightgray border-softblue text-sm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="rounded-none">Cancelar</Button>
            <Button data-testid="admin-ads-campaign-save" onClick={save} className="bg-nightblue text-white rounded-none uppercase text-xs tracking-widest font-semibold">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar campaña?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{confirm?.name}</strong>. Si tiene piezas asociadas, la operación será rechazada.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// -------------------------- CREATIVES TAB --------------------------
function CreativesTab() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [slots, setSlots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [diagnose, setDiagnose] = useState(null);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    if (!diagnose) { setDiagData(null); return; }
    let cancelled = false;
    setDiagLoading(true);
    api.adminDiagnoseCreative(diagnose.id, { device: "desktop" })
      .then((d) => { if (!cancelled) setDiagData(d); })
      .catch(() => { if (!cancelled) toast.error("Error al diagnosticar"); })
      .finally(() => { if (!cancelled) setDiagLoading(false); });
    return () => { cancelled = true; };
  }, [diagnose]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.adminListCreatives(), api.adminListCampaigns(), api.adminListAdSlots(), api.categories()])
      .then(([c, camps, sl, cats]) => {
        setItems(c);
        setCampaigns(camps);
        setSlots(sl);
        setCategories(cats);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const openNew = () => setDialog({
    name: "", campaign_id: campaigns[0]?.id || "", slot_id: slots[0]?.id || "",
    creative_type: "image", asset_url: "", html_code: "", target_url: "", alt: "",
    start_at: "", end_at: "", active: true, priority: 5,
    devices: ["desktop", "tablet", "mobile"], category_slugs: [], article_ids: [],
    impression_limit: "", click_limit: "",
  });

  const save = async () => {
    if (!dialog.name.trim() || !dialog.campaign_id || !dialog.slot_id) {
      toast.error("Nombre, campaña y ubicación son obligatorios.");
      return;
    }
    try {
      const payload = {
        ...dialog,
        start_at: fromLocalInput(dialog.start_at),
        end_at: fromLocalInput(dialog.end_at),
        impression_limit: dialog.impression_limit ? parseInt(dialog.impression_limit, 10) : null,
        click_limit: dialog.click_limit ? parseInt(dialog.click_limit, 10) : null,
        priority: parseInt(dialog.priority || 5, 10),
      };
      if (dialog.id) await api.adminUpdateCreative(dialog.id, payload);
      else await api.adminCreateCreative(payload);
      toast.success("Pieza guardada.");
      setDialog(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    }
  };

  const runAction = async (id, action) => {
    try {
      await api.adminCreativeAction(id, action);
      toast.success("Acción aplicada.");
      load();
    } catch { toast.error("No se pudo aplicar."); }
  };

  const del = async () => {
    try {
      await api.adminDeleteCreative(confirm.id);
      toast.success("Pieza eliminada.");
      setConfirm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  const toggleDevice = (dev) => {
    const cur = new Set(dialog.devices);
    if (cur.has(dev)) cur.delete(dev); else cur.add(dev);
    setDialog({ ...dialog, devices: Array.from(cur) });
  };

  const isMedia = dialog && ["image", "gif", "video"].includes(dialog.creative_type);

  return (
    <div data-testid="admin-ads-creatives">
      <div className="flex justify-end mb-4">
        <Button
          data-testid="admin-ads-creative-new"
          onClick={openNew}
          disabled={campaigns.length === 0 || slots.length === 0}
          className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 uppercase text-xs tracking-widest font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva pieza
        </Button>
      </div>

      <div className="bg-white border border-softblue overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lightgray text-textgray text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-3 w-16">Preview</th>
              <th className="text-left px-3 py-3">Nombre</th>
              <th className="text-left px-3 py-3">Tipo</th>
              <th className="text-left px-3 py-3">Campaña</th>
              <th className="text-left px-3 py-3">Ubicación</th>
              <th className="text-left px-3 py-3">Vigencia</th>
              <th className="text-left px-3 py-3">Impr / Clics</th>
              <th className="text-center px-3 py-3">Estado</th>
              <th className="text-right px-3 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-textgray">Cargando…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-textgray">Todavía no hay piezas.</td></tr>}
            {!loading && items.map((c) => {
              const campaign = campaigns.find((x) => x.id === c.campaign_id);
              const slot = slots.find((x) => x.id === c.slot_id);
              const isVis = ["image", "gif", "video"].includes(c.creative_type);
              return (
                <tr key={c.id} data-testid={`admin-ads-creative-row-${c.id}`} className="border-t border-softblue hover:bg-lightgray/40">
                  <td className="px-3 py-2">
                    {isVis && c.asset_url ? (
                      <img src={c.asset_url} alt="" className="w-12 h-12 object-cover" loading="lazy" />
                    ) : (
                      <div className="w-12 h-12 bg-lightgray border border-softblue flex items-center justify-center text-[10px] text-textgray font-mono">{c.creative_type}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-blueblack line-clamp-1">{c.name}</div>
                    {c.target_url && <div className="text-[11px] text-textgray truncate max-w-xs">{c.target_url}</div>}
                  </td>
                  <td className="px-3 py-3 text-textgray text-xs uppercase tracking-widest">{c.creative_type}</td>
                  <td className="px-3 py-3 text-textgray">{campaign?.name || "—"}</td>
                  <td className="px-3 py-3 text-textgray">{slot?.name || "—"}</td>
                  <td className="px-3 py-3 text-textgray text-xs">
                    {c.start_at ? formatDateShort(c.start_at) : "—"} → {c.end_at ? formatDateShort(c.end_at) : "sin fin"}
                  </td>
                  <td className="px-3 py-3 text-textgray text-xs">
                    {c.impressions} / {c.clicks}
                    {c.impressions > 0 && ` (${((c.clicks / c.impressions) * 100).toFixed(1)}%)`}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${c.active ? "bg-watergreen/20 text-nightblue" : "bg-yellow-100 text-yellow-800"}`}>
                      {c.active ? "Activa" : "Pausada"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setPreview(c)} title="Previsualizar" className="h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray inline-flex items-center justify-center">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDiagnose(c)}
                        title="Diagnosticar publicación"
                        data-testid={`admin-ads-creative-diagnose-${c.id}`}
                        className="h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray inline-flex items-center justify-center"
                      >
                        <Stethoscope className="w-4 h-4" />
                      </button>
                      <button onClick={() => runAction(c.id, c.active ? "pause" : "activate")} title={c.active ? "Pausar" : "Activar"} className="h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray inline-flex items-center justify-center">
                        {c.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setDialog({ ...c, start_at: toLocalInput(c.start_at), end_at: toLocalInput(c.end_at), impression_limit: c.impression_limit || "", click_limit: c.click_limit || "" })}
                        data-testid={`admin-ads-creative-edit-${c.id}`}
                        className="h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray inline-flex items-center justify-center"
                      ><Edit3 className="w-4 h-4" /></button>
                      <button
                        onClick={() => setConfirm(c)}
                        className="h-8 w-8 border border-softblue text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Editor dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">{dialog?.id ? "Editar pieza" : "Nueva pieza publicitaria"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Nombre *</Label>
                <Input data-testid="admin-ads-creative-name" value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Campaña *</Label>
                <select data-testid="admin-ads-creative-campaign" value={dialog.campaign_id} onChange={(e) => setDialog({ ...dialog, campaign_id: e.target.value })} className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.client || "—"}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Ubicación *</Label>
                <select data-testid="admin-ads-creative-slot" value={dialog.slot_id} onChange={(e) => setDialog({ ...dialog, slot_id: e.target.value })} className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                  {slots.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Tipo</Label>
                <select data-testid="admin-ads-creative-type" value={dialog.creative_type} onChange={(e) => setDialog({ ...dialog, creative_type: e.target.value })} className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                  {CREATIVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Prioridad</Label>
                <Input type="number" min={0} max={99} value={dialog.priority} onChange={(e) => setDialog({ ...dialog, priority: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>

              {isMedia ? (
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Archivo</Label>
                  <div className="flex gap-2 items-start">
                    <Input value={dialog.asset_url || ""} onChange={(e) => setDialog({ ...dialog, asset_url: e.target.value })} placeholder="https://…" className="flex-1 h-9 bg-lightgray border-softblue font-mono text-xs" />
                    <Button type="button" onClick={() => setPickerOpen(true)} className="bg-nightblue text-white rounded-none h-9 uppercase text-xs tracking-widest font-semibold">
                      <ImageIcon className="w-4 h-4 mr-2" /> Biblioteca
                    </Button>
                  </div>
                  {dialog.asset_url && (
                    <div className="border border-softblue bg-lightgray max-h-48 overflow-hidden">
                      {dialog.creative_type === "video" ? (
                        <video src={dialog.asset_url} controls className="max-h-48 mx-auto" />
                      ) : (
                        <img src={dialog.asset_url} alt="" className="max-h-48 mx-auto" />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Código HTML / embed</Label>
                  <Textarea data-testid="admin-ads-creative-html" value={dialog.html_code || ""} onChange={(e) => setDialog({ ...dialog, html_code: e.target.value })} rows={5} className="mt-1 bg-lightgray border-softblue font-mono text-xs" placeholder='<iframe src="https://www.youtube.com/embed/..."></iframe>' />
                  <p className="text-[11px] text-textgray mt-1">Solo se aceptan iframes de YouTube/Vimeo/Google Ads. Scripts y handlers on* son descartados por seguridad.</p>
                </div>
              )}

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">URL de destino</Label>
                <Input data-testid="admin-ads-creative-target" value={dialog.target_url || ""} onChange={(e) => setDialog({ ...dialog, target_url: e.target.value })} placeholder="https://…" className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Texto alternativo</Label>
                <Input value={dialog.alt} onChange={(e) => setDialog({ ...dialog, alt: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Inicio</Label>
                <Input type="datetime-local" value={dialog.start_at} onChange={(e) => setDialog({ ...dialog, start_at: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Fin</Label>
                <Input type="datetime-local" value={dialog.end_at} onChange={(e) => setDialog({ ...dialog, end_at: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Límite impresiones</Label>
                <Input type="number" min={0} value={dialog.impression_limit} onChange={(e) => setDialog({ ...dialog, impression_limit: e.target.value })} placeholder="Sin límite" className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Límite clics</Label>
                <Input type="number" min={0} value={dialog.click_limit} onChange={(e) => setDialog({ ...dialog, click_limit: e.target.value })} placeholder="Sin límite" className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold mb-1 block">Dispositivos habilitados</Label>
                <div className="flex gap-2">
                  {["desktop", "tablet", "mobile"].map((dev) => (
                    <button key={dev} type="button" onClick={() => toggleDevice(dev)} className={`flex-1 h-9 border uppercase text-xs tracking-widest font-semibold ${dialog.devices.includes(dev) ? "bg-watergreen/20 border-watergreen text-nightblue" : "bg-white border-softblue text-textgray hover:bg-lightgray"}`}>
                      {dev === "desktop" ? "Escritorio" : dev === "tablet" ? "Tablet" : "Móvil"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold mb-1 block">Categorías (opcional)</Label>
                <select multiple value={dialog.category_slugs} onChange={(e) => setDialog({ ...dialog, category_slugs: Array.from(e.target.selectedOptions).map((o) => o.value) })} className="w-full h-24 bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                  {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                </select>
                <p className="text-[11px] text-textgray mt-1">Vacío = todas las categorías.</p>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between border-t border-softblue pt-3">
                <label className="flex items-center gap-3 text-sm">
                  <Switch checked={dialog.active} onCheckedChange={(v) => setDialog({ ...dialog, active: !!v })} data-testid="admin-ads-creative-active" />
                  <span className="text-nightblue font-semibold">Activa</span>
                </label>
                <span className="text-[11px] text-textgray">{(dialog.article_ids || []).length} noticia(s) específica(s)</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="rounded-none">Cancelar</Button>
            <Button data-testid="admin-ads-creative-save" onClick={save} className="bg-nightblue text-white rounded-none uppercase text-xs tracking-widest font-semibold">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dialog && (
        <MediaPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={(m) => setDialog({ ...dialog, asset_url: m.url, alt: dialog.alt || m.alt || "" })}
          title="Elegir archivo de la biblioteca"
          accept="image"
        />
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Vista previa: {preview?.name}</DialogTitle>
          </DialogHeader>
          <div className="bg-lightgray border border-softblue p-6 flex items-center justify-center">
            {preview?.creative_type === "video" && preview?.asset_url && (
              <video src={preview.asset_url} controls className="max-h-96 max-w-full" />
            )}
            {["image", "gif"].includes(preview?.creative_type) && preview?.asset_url && (
              <img src={preview.asset_url} alt={preview.alt} className="max-h-96 max-w-full" />
            )}
            {["html", "embed"].includes(preview?.creative_type) && preview?.html_code && (
              <div className="w-full" dangerouslySetInnerHTML={{ __html: preview.html_code }} />
            )}
          </div>
          {preview?.target_url && <p className="text-xs text-textgray text-center mt-2">Link: {preview.target_url}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} className="rounded-none">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pieza?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{confirm?.name}</strong> y sus registros de eventos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diagnostic modal */}
      <Dialog open={!!diagnose} onOpenChange={(o) => !o && setDiagnose(null)}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-turquoise" />
              Diagnóstico: {diagnose?.name}
            </DialogTitle>
          </DialogHeader>
          {diagLoading && (
            <div className="py-6 flex items-center gap-2 text-textgray">
              <Loader2 className="w-4 h-4 animate-spin" /> Verificando cada regla…
            </div>
          )}
          {diagData && !diagLoading && (
            <div className="space-y-3">
              <div className={`px-4 py-3 border ${diagData.publishable ? "bg-watergreen/20 border-watergreen text-nightblue" : "bg-red-50 border-red-300 text-red-800"}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold">
                  {diagData.publishable ? "✓ Publicable" : "✗ NO Publicable"}
                </div>
                <div className="font-semibold">
                  {diagData.publishable
                    ? "La pieza cumple con todas las reglas y aparece en el portal."
                    : `Motivo bloqueante: ${diagData.reason}`}
                </div>
              </div>
              <div className="border border-softblue divide-y divide-softblue text-sm max-h-[50vh] overflow-y-auto">
                {diagData.checks.map((c, i) => (
                  <div key={i} className={`flex items-start gap-2 px-3 py-2 ${!c.ok && c.blocking ? "bg-red-50" : !c.ok ? "bg-yellow-50" : ""}`}>
                    <span className="flex-shrink-0 w-5">{c.ok ? "✅" : (c.blocking ? "❌" : "⚠️")}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-nightblue uppercase text-[10px] tracking-widest">
                        {c.check.replace(/_/g, " ")} {c.blocking ? "" : "(no bloqueante)"}
                      </div>
                      <div className="text-textgray text-[12px]">{c.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-textgray">
                Se evaluó suponiendo <b>desktop</b>. Los checks marcados con ❌ y &quot;(BLOQ)&quot; impiden la publicación.
                Los ⚠️ son advertencias (targeting específico, límites, etc.).
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiagnose(null)} className="rounded-none">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------------- SLOTS TAB --------------------------
function SlotsTab() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.adminListAdSlots().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    if (!dialog.name.trim()) { toast.error("El nombre es obligatorio."); return; }
    try {
      if (dialog.id) await api.adminUpdateAdSlot(dialog.id, dialog);
      else await api.adminCreateAdSlot(dialog);
      toast.success("Ubicación guardada.");
      setDialog(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    }
  };
  const del = async () => {
    const doDelete = async (force = false) => {
      try {
        await api.adminDeleteAdSlot(confirm.id, force);
        toast.success(force ? "Ubicación eliminada (piezas incluidas)" : "Ubicación eliminada.");
        setConfirm(null); load();
      } catch (e) {
        const status = e?.response?.status;
        const d = e?.response?.data?.detail;
        if (!force && status === 409 && typeof d === "object" && d?.requires_force) {
          const msg = d.message || `Hay ${d.linked_count || "varias"} piezas asociadas.`;
          if (window.confirm(`${msg}\n\n¿Continuar y eliminar todo?`)) return doDelete(true);
          setConfirm(null);
          return;
        }
        toast.error(typeof d === "string" ? d : (d?.message || "No se pudo eliminar."));
      }
    };
    await doDelete(false);
  };

  return (
    <div data-testid="admin-ads-slots">
      <div className="flex justify-end mb-4">
        <Button
          data-testid="admin-ads-slot-new"
          onClick={() => setDialog({ name: "", slug: "", description: "", default_position: "custom", device_target: "all", active: true })}
          className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 uppercase text-xs tracking-widest font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva ubicación
        </Button>
      </div>
      <div className="bg-white border border-softblue overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lightgray text-textgray text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-3">Nombre</th>
              <th className="text-left px-3 py-3">Slug</th>
              <th className="text-left px-3 py-3">Posición</th>
              <th className="text-left px-3 py-3">Dispositivo</th>
              <th className="text-left px-3 py-3">Piezas</th>
              <th className="text-center px-3 py-3">Activa</th>
              <th className="text-right px-3 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-textgray">Cargando…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-textgray">Todavía no hay ubicaciones.</td></tr>}
            {!loading && items.map((s) => (
              <tr key={s.id} className="border-t border-softblue hover:bg-lightgray/40">
                <td className="px-3 py-3 font-semibold text-blueblack">{s.name}</td>
                <td className="px-3 py-3 font-mono text-xs text-textgray">{s.slug}</td>
                <td className="px-3 py-3 text-textgray text-xs">{SLOT_POSITIONS.find((p) => p.value === s.default_position)?.label || s.default_position}</td>
                <td className="px-3 py-3 text-textgray text-xs uppercase">{s.device_target}</td>
                <td className="px-3 py-3 text-textgray">{s.creatives_count || 0}</td>
                <td className="px-3 py-3 text-center">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${s.active ? "bg-watergreen/20 text-nightblue" : "bg-yellow-100 text-yellow-800"}`}>
                    {s.active ? "Sí" : "No"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setDialog(s)} className="h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray inline-flex items-center justify-center"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => setConfirm(s)} className="h-8 w-8 border border-softblue text-red-600 hover:bg-red-50 inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">{dialog?.id ? "Editar ubicación" : "Nueva ubicación"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Nombre *</Label>
                <Input value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} className="mt-1 h-9 bg-lightgray border-softblue text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Slug (opcional)</Label>
                <Input value={dialog.slug || ""} onChange={(e) => setDialog({ ...dialog, slug: e.target.value })} placeholder="se genera del nombre" className="mt-1 h-9 bg-lightgray border-softblue font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción</Label>
                <Textarea value={dialog.description || ""} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} rows={2} className="mt-1 bg-lightgray border-softblue text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Posición</Label>
                  <select value={dialog.default_position} onChange={(e) => setDialog({ ...dialog, default_position: e.target.value })} className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                    {SLOT_POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Dispositivos</Label>
                  <select value={dialog.device_target} onChange={(e) => setDialog({ ...dialog, device_target: e.target.value })} className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue">
                    <option value="all">Todos</option>
                    <option value="desktop">Solo escritorio</option>
                    <option value="tablet">Solo tablet</option>
                    <option value="mobile">Solo móvil</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm">
                <Switch checked={dialog.active} onCheckedChange={(v) => setDialog({ ...dialog, active: !!v })} />
                <span className="text-nightblue font-semibold">Activa</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="rounded-none">Cancelar</Button>
            <Button onClick={save} className="bg-nightblue text-white rounded-none uppercase text-xs tracking-widest font-semibold">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar ubicación?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{confirm?.name}</strong>. Si tiene piezas asociadas, será rechazado.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Reusable stat card — defined at module scope so React reuses the same
// component type across StatsTab renders (avoids full subtree remount).
const Stat = ({ label, value, sub }) => (
  <div className="bg-white border border-softblue p-5">
    <div className="text-xs uppercase tracking-widest text-textgray font-semibold">{label}</div>
    <div className="mt-2 font-serif font-black text-3xl text-blueblack">{value}</div>
    {sub && <div className="text-xs text-textgray mt-1">{sub}</div>}
  </div>
);

// -------------------------- STATS TAB --------------------------
function StatsTab() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.adminAdsStats({ days })
      .then((s) => { if (!cancelled) setStats(s); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) return <div className="p-6 text-sm text-textgray">Cargando estadísticas…</div>;
  if (!stats) return <div className="p-6 text-sm text-textgray">Sin datos.</div>;

  return (
    <div data-testid="admin-ads-stats" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-textgray">Últimos {days} días</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 h-8 text-xs uppercase tracking-widest font-semibold border ${
                days === d ? "bg-nightblue text-white border-nightblue" : "border-softblue text-nightblue hover:bg-lightgray"
              }`}
            >{d} días</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Impresiones" value={stats.totals.impressions.toLocaleString("es-AR")} />
        <Stat label="Clics" value={stats.totals.clicks.toLocaleString("es-AR")} />
        <Stat label="CTR" value={`${stats.totals.ctr.toFixed(2)}%`} sub="Clic-through rate" />
      </div>

      {stats.by_creative.length > 0 && (
        <div className="bg-white border border-softblue">
          <div className="px-4 py-3 border-b border-softblue font-serif text-lg font-bold text-blueblack">Rendimiento por pieza</div>
          <PerfTable rows={stats.by_creative} />
        </div>
      )}

      {stats.by_slot.length > 0 && (
        <div className="bg-white border border-softblue">
          <div className="px-4 py-3 border-b border-softblue font-serif text-lg font-bold text-blueblack">Rendimiento por ubicación</div>
          <PerfTable rows={stats.by_slot} />
        </div>
      )}
    </div>
  );
}

function PerfTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-lightgray text-textgray text-[11px] uppercase tracking-widest">
        <tr>
          <th className="text-left px-3 py-3">Nombre</th>
          <th className="text-right px-3 py-3">Impresiones</th>
          <th className="text-right px-3 py-3">Clics</th>
          <th className="text-right px-3 py-3">CTR</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t border-softblue">
            <td className="px-3 py-2 font-semibold text-blueblack">{r.name || r.key}</td>
            <td className="px-3 py-2 text-right text-nightblue">{r.impressions.toLocaleString("es-AR")}</td>
            <td className="px-3 py-2 text-right text-nightblue">{r.clicks.toLocaleString("es-AR")}</td>
            <td className="px-3 py-2 text-right text-nightblue">{r.ctr.toFixed(2)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
